import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  autoHide?: boolean;
}

interface UIState {
  // Sidebar state (desktop)
  sidebarOpen: boolean;

  // Mobile drawer state
  mobileDrawerOpen: boolean;

  // Theme state
  currentTheme: 'light' | 'dark';

  // Language state
  language: string;

  // Notifications
  notifications: Notification[];

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleMobileDrawer: () => void;
  setMobileDrawerOpen: (open: boolean) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: string) => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

/**
 * UI state store for global UI-related state management
 * Persists theme and language preferences
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarOpen: false,
      mobileDrawerOpen: false,
      currentTheme: 'light',
      language: 'en',
      notifications: [],

      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      toggleMobileDrawer: () => {
        set((state) => ({ mobileDrawerOpen: !state.mobileDrawerOpen }));
      },

      setMobileDrawerOpen: (open: boolean) => {
        set({ mobileDrawerOpen: open });
      },

      setTheme: (theme: 'light' | 'dark') => {
        set({ currentTheme: theme });
      },

      setLanguage: (language: string) => {
        set({ language });
      },

      addNotification: (notification) => {
        const id = Date.now().toString();
        set((state) => ({
          notifications: [...state.notifications, { ...notification, id }],
        }));

        // Auto-remove after 5 seconds if autoHide is true (default)
        if (notification.autoHide !== false) {
          setTimeout(() => {
            get().removeNotification(id);
          }, 5000);
        }
      },

      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },
    }),
    {
      name: 'lecturo-ui-store',
      // Only persist theme and language preferences
      partialize: (state) => ({
        currentTheme: state.currentTheme,
        language: state.language,
      }),
    }
  )
);
